import { useIsSmOrSmaller } from '@dreamstack/feature-components/src/hooks/useWindowSize'
import { HeadingH3 } from '@dreamstack/feature-components/src/layout/Texts'
import { useTranslation } from '@dreamstack/i18n'
import type {
  ScatterPlotNodeProps as NodeProps,
  ScatterPlotDatum,
} from '@nivo/scatterplot'
import { animated } from '@react-spring/web'
import first from 'lodash/first'
import last from 'lodash/last'
import map from 'lodash/map'
import range from 'lodash/range'
import dynamic from 'next/dynamic'
import type { FunctionComponent } from 'react'
import { Fragment } from 'react'
import { GraphLegends } from './HomeOwnershipReport/GraphLegends'
import { useGraphFontsizeCb } from './HomeOwnershipReport/useGraphFontsize'
import {
  SCATTERPLOT_MIN_MAX_MARKER_COLOR,
  SCATTERPLOT_MIN_MAX_RECT_FILL_COLOR,
  SCATTERPLOT_NODE_COLOR,
} from './RegionGraphs/libs/colors'
import { useGetMinMaxDataValues } from './RegionGraphs/libs/getMinMaxDataValues'
import { getYScaleValue } from './RegionGraphs/libs/getYScaleValue'
import type {
  CandleStickMinMaxData,
  DataPoint,
} from './RegionGraphs/libs/typings'
import { useGetFormattedToLocal } from './RegionGraphs/libs/useGetFormattedToLocal'

// variables
const MIN_MAX_RECT_WIDTH = 1
const MIN_MAX_MARKER_WIDTH = 30
const MIN_MAX_X_OFFSET = 20

const ResponsiveScatterPlot = dynamic(
  () => import('@nivo/scatterplot').then((mod) => mod.ResponsiveScatterPlot),
  {
    ssr: false,
  }
)

// create square node, because nivo by default creates circular points in graph
const SquareNode = (props: NodeProps<ScatterPlotDatum>) => {
  const node = props.node
  const AnimatedRect = animated.rect as any
  return (
    <AnimatedRect
      x={node.x - node.size / 2}
      y={node.y - node.size / 2}
      width={node.size}
      height={node.size}
      fill={node.color}
      style={{ mixBlendMode: props.blendMode }}
    />
  )
}

const CircleNode = (props: NodeProps<ScatterPlotDatum>) => {
  const node = props.node
  const AnimatedCircle = animated.circle as any
  return (
    <AnimatedCircle
      cx={node.x}
      cy={node.y}
      r={node.size / 2}
      fill={node.color}
      style={{ mixBlendMode: props.blendMode }}
    />
  )
}

export interface ScatterPlotWithRegressionLineProps {
  data: CandleStickMinMaxData[]
  title: string
  titleSubject: string
  rangeStep: number
  hasMinMaxMarker?: boolean
}

export const ScatterPlotWithRegressionLine: FunctionComponent<
  React.PropsWithChildren<ScatterPlotWithRegressionLineProps>
> = ({ data, title, titleSubject, rangeStep, hasMinMaxMarker }) => {
  const graphFontSize = useGraphFontsizeCb()
  const t = useTranslation()
  const firstSerieId = data[0].id
  const formatToLocalString = useGetFormattedToLocal()
  // check if Windows is sm or smaller, so that we can hide the labels next to each point if window too small
  const isMobile = useIsSmOrSmaller()

  const { maxXDataValue, minXDataValue, maxYDataValue, minYDataValue } =
    useGetMinMaxDataValues(data)

  const yScaleValues = range(
    getYScaleValue(minYDataValue, rangeStep),
    getYScaleValue(maxYDataValue, rangeStep, true) + 1,
    rangeStep
  )

  const xScaleValues = range(minXDataValue, maxXDataValue + 1, 2)

  const CandlesticksLayer = ({
    xScale,
    yScale,
  }: {
    xScale: any
    yScale: any
  }) => {
    if (!hasMinMaxMarker || isMobile) return null
    return (
      <Fragment>
        {data.map((serie: any, i: number) => {
          const isLeft = i === 0

          return serie.data.map((point: any, i: number) => {
            const x0 = xScale(point.x)
            const y0 = yScale(point.low)
            const y1 = yScale(point.high)

            const width = MIN_MAX_RECT_WIDTH
            const height = y0 - y1

            const spanX = isLeft
              ? x0 - MIN_MAX_RECT_WIDTH / 2 - MIN_MAX_X_OFFSET
              : x0 - MIN_MAX_RECT_WIDTH / 2 + MIN_MAX_X_OFFSET

            const markerX1 = isLeft
              ? x0 - MIN_MAX_MARKER_WIDTH / 2 - MIN_MAX_X_OFFSET
              : x0 - MIN_MAX_MARKER_WIDTH / 2 + MIN_MAX_X_OFFSET

            const markerX2 = isLeft
              ? x0 + MIN_MAX_MARKER_WIDTH / 2 - MIN_MAX_X_OFFSET
              : x0 + MIN_MAX_MARKER_WIDTH / 2 + MIN_MAX_X_OFFSET

            return (
              <g key={i}>
                <rect
                  x={spanX}
                  y={y1}
                  width={width}
                  height={height}
                  fill={
                    isLeft
                      ? SCATTERPLOT_MIN_MAX_RECT_FILL_COLOR[0]
                      : SCATTERPLOT_MIN_MAX_RECT_FILL_COLOR[1]
                  }
                  stroke={
                    isLeft
                      ? SCATTERPLOT_MIN_MAX_MARKER_COLOR[0]
                      : SCATTERPLOT_MIN_MAX_MARKER_COLOR[1]
                  }
                  strokeWidth={2}
                />
                <line
                  x1={markerX1}
                  y1={y0}
                  x2={markerX2}
                  y2={y0}
                  stroke={
                    isLeft
                      ? SCATTERPLOT_MIN_MAX_MARKER_COLOR[0]
                      : SCATTERPLOT_MIN_MAX_MARKER_COLOR[1]
                  }
                  strokeWidth={4}
                />
                <line
                  x1={markerX1}
                  y1={y1}
                  x2={markerX2}
                  y2={y1}
                  stroke={
                    isLeft
                      ? SCATTERPLOT_MIN_MAX_MARKER_COLOR[0]
                      : SCATTERPLOT_MIN_MAX_MARKER_COLOR[1]
                  }
                  strokeWidth={4}
                />
              </g>
            )
          })
        })}
      </Fragment>
    )
  }

  const RegressionLine = ({ xScale, yScale }: { xScale: any; yScale: any }) => {
    return (
      <Fragment>
        {map(data, (serie, i) => {
          const firstPoint: DataPoint = first(serie.data) as any
          const lastPoint: DataPoint = last(serie.data) as any
          const x1 = xScale(firstPoint.x)
          const x2 = xScale(lastPoint.x)
          const y1 = yScale(first(serie.lineValues))
          const y2 = yScale(last(serie.lineValues))
          return (
            <line
              key={i}
              x1={x1}
              x2={x2}
              y1={y1}
              y2={y2}
              strokeWidth={3}
              stroke={i === 0 ? '#778AA6' : '#B6A6BA'}
            />
          )
        })}
      </Fragment>
    )
  }

  return (
    <div className="col-span-full bg-accentroAqua-100 h-auto lg:col-start-3 lg:col-span-8">
      <div className="ml-px-40 pt-px-32">
        <HeadingH3 className="mb-px-48">{title}</HeadingH3>
      </div>
      <div className="flex justify-center">
        <GraphLegends
          identifiers={[
            {
              legend: t(
                'accentro:districtLandingPageGraphs.regionAverageGraph.newBuild'
              ),
              color: SCATTERPLOT_NODE_COLOR[0],
              isCircle: true,
            },
            {
              legend: t(
                'accentro:districtLandingPageGraphs.regionAverageGraph.existingBuilding'
              ),
              color: SCATTERPLOT_NODE_COLOR[1],
            },
          ]}
        />
      </div>
      <div className="h-px-576">
        <ResponsiveScatterPlot
          data={data}
          margin={{ top: 30, right: 50, bottom: 70, left: 90 }}
          xScale={{ type: 'linear', min: minXDataValue - 1 }}
          yScale={{
            type: 'linear',
            min: getYScaleValue(minYDataValue, rangeStep),
            max: getYScaleValue(maxYDataValue, rangeStep, true),
          }}
          theme={{
            text: {
              fontSize: graphFontSize(),
              fontFamily: 'Montserrat',
            },
            axis: {
              legend: {
                text: {
                  fontSize: '20px',
                  fontWeight: 500,
                },
              },
            },
          }}
          gridXValues={xScaleValues}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: t(
              'accentro:districtLandingPageGraphs.regionAverageGraph.year'
            ),
            legendPosition: 'middle',
            legendOffset: 46,
            tickValues: xScaleValues,
          }}
          gridYValues={yScaleValues}
          axisLeft={{
            tickValues: yScaleValues,
            format: (val) => formatToLocalString(val),
          }}
          colors={SCATTERPLOT_NODE_COLOR}
          nodeComponent={(node) => {
            const customNo =
              node.node.serieId === firstSerieId ? CircleNode : SquareNode
            return customNo(node)
          }}
          nodeSize={isMobile ? 7.5 : 15}
          layers={[
            'grid',
            'axes',
            'nodes',
            'markers',
            RegressionLine,
            CandlesticksLayer,
            'mesh',
            'legends',
          ]}
          tooltip={({ node }) => {
            const data = node.data as DataPoint
            return (
              <div
                style={{
                  color: node.color,
                  background: '#fff',
                  padding: '12px 16px',
                }}
              >
                <strong>{node.serieId}</strong>
                <br />
                <strong>
                  {t(
                    'accentro:districtLandingPageGraphs.regionAverageGraph.year'
                  )}
                  :{' '}
                </strong>
                {`${node.formattedX}`}
                <br />
                <strong>
                  {`${t(
                    'accentro:districtLandingPageGraphs.scatterPlotWithRegressionLine.average'
                  )} ${titleSubject}: `}{' '}
                </strong>
                {`${formatToLocalString(node.data.y)} EUR`}
                {hasMinMaxMarker && (
                  <>
                    <br />
                    <strong>
                      {`${t(
                        'accentro:districtLandingPageGraphs.scatterPlotWithRegressionLine.lowest'
                      )} ${titleSubject}: `}{' '}
                    </strong>
                    {`${formatToLocalString(data.low)} EUR`}
                    <br />
                    <strong>{`${t(
                      'accentro:districtLandingPageGraphs.scatterPlotWithRegressionLine.highest'
                    )} ${titleSubject}: `}</strong>
                    {`${formatToLocalString(data.high)} EUR`}
                  </>
                )}
              </div>
            )
          }}
        />
      </div>
    </div>
  )
}
